import {useMemo} from 'react';
import {
    formatVersionNumber,
    parseVersionFromSnapshotPath,
    useReportDocumentSubscription,
    useReportVersionDocSubscription,
    useReportVersionsSubscription
} from '../previewPage/useReportDocumentSubscription';
import {useVersionBlob} from '../previewPage/useVersionBlob';
import {apiMetaToHeaderData, mergeHeaderData} from '../../../utils/documentHeaderMeta';
import usePreviewAuthors from '../previewPage/usePreviewAuthors';

/**
 * Custom hook to load approved report data
 * Similar to useVersionManagement but without URL search param manipulation
 * Always loads the latest version for approved reports
 */
const useApprovedReportData = (reportId) => {
    // Subscribe to report document (base doc)
    const {data: reportDoc, loading: docLoading, error: docError} = useReportDocumentSubscription(reportId);

    // Subscribe to versions collection (new backend structure)
    const {data: versions, loading: versionsLoading, error: versionsError} = useReportVersionsSubscription(reportId);

    // Latest version comes from the first item (ordered desc). Fallback to snapshotPath for safety.
    const latestVersion = useMemo(() => {
        const first = versions?.[0];
        const fromCollection = formatVersionNumber(first?.version);
        if (fromCollection) return fromCollection;
        return parseVersionFromSnapshotPath(reportDoc?.snapshotPath);
    }, [versions, reportDoc?.snapshotPath]);

    // Always use latest version for approved reports (no URL param needed)
    const version = latestVersion;

    // Subscribe to selected version document (header/meta should come from here)
    const selectedVersionId = version || null;
    const {
        data: versionDoc,
        loading: versionDocLoading,
        error: versionDocError
    } = useReportVersionDocSubscription(reportId, selectedVersionId);

    // Download and parse blob for the selected version; metadata carries the saved document header
    const {content, references, metadata: versionMetadata, loading: blobLoading, error: blobError} = useVersionBlob(reportId, version);

    // For preview header/meta we prefer versionDoc.report (new schema), fallback to base report doc.
    const headerDoc = versionDoc?.report || reportDoc;
    const meta = headerDoc?.meta;

    // Compute document header: prefer saved version metadata, fall back to report meta
    const documentHeader = useMemo(() => {
        if (versionMetadata && typeof versionMetadata === 'object' && Object.keys(versionMetadata).length > 0) {
            return mergeHeaderData(apiMetaToHeaderData(versionMetadata), apiMetaToHeaderData(meta));
        }
        return apiMetaToHeaderData(meta);
    }, [versionMetadata, meta]);

    // Resolve authors from content attribution, version metadata, and report entity
    const {authors: previewAuthors} = usePreviewAuthors({
        content,
        versionDoc,
        meta,
        documentHeader,
        reportDoc,
        reportId,
        version,
        latestVersion,
    });

    // Combined loading and error states
    const isLoading = docLoading || versionsLoading || versionDocLoading || (version && blobLoading && !content);
    const error = docError || versionsError || versionDocError || blobError;

    return {
        version,
        latestVersion,
        versions,
        reportDoc,
        versionDoc,
        headerDoc,
        content,
        references,
        meta,
        documentHeader,
        previewAuthors,
        isLoading,
        error,
        docLoading,
        versionsLoading,
        versionDocLoading
    };
};

export default useApprovedReportData;
